import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SignInPage } from '../components/ui/sign-in-page';
import { getPortalHomePath } from '../utils/portalPaths';
import { handleStudentPostLogin } from '../utils/postLogin';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SeoHead from '../components/SeoHead';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { currentUser, isAdmin, logIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      navigate(getPortalHomePath({ isAdmin }), { replace: true });
    }
  }, [currentUser, isAdmin, navigate]);

  function handleInputChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const cred = await logIn(formData.email, formData.password, formData.rememberMe);
      const profileSnap = await getDoc(doc(db, 'users', cred.user.uid));
      const role = profileSnap.exists() ? profileSnap.data().role : 'student';
      const userIsAdmin = role === 'admin';

      if (!userIsAdmin) {
        const result = await handleStudentPostLogin(cred.user);
        if (result.blocked) {
          navigate('/portal-blocked', { replace: true });
          return;
        }
        if (result.devicePending) {
          navigate('/device-pending', { replace: true });
          return;
        }
      }
      navigate(getPortalHomePath({ isAdmin: userIsAdmin }), { replace: true });
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  }

  function getErrorMessage(code) {
    switch (code) {
      case 'auth/user-not-found':
        return 'No account found with this email. Contact Success Bridge Academy for your login details.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait and try again.';
      case 'auth/invalid-credential':
        return 'Invalid email or password. Use the login details sent to you by the academy.';
      case 'auth/operation-not-allowed':
      case 'auth/admin-restricted-operation':
        return 'Email/password sign-in is not enabled. Please enable Email/Password in Firebase Console → Authentication → Sign-in method.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      default:
        return `Failed to sign in (${code || 'unknown'}). Please try again.`;
    }
  }

  return (
    <>
      <SeoHead />
      <SignInPage
        formData={formData}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        error={error}
        loading={loading}
        onForgot={() => navigate('/forgot-password')}
        onSocial={() =>
          setError('Please sign in with your Success Bridge email and password. Google and GitHub are not enabled for the portal.')
        }
      />
    </>
  );
}
